import {Injectable, NgZone} from '@angular/core';
import {FeaturesRepositoryService} from '../../common/service/features-repository.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {SpatialQueryService} from './spatial-query.service';
import {LookupService} from '../../common/service/lookup.service';
import {LayerDefinitionService} from '../esri-nlr/layers/definitions/layer-definition-service';
import {TraitsService} from '../../common/service/traits.service';
import {AppConfigService} from '../../common/service/app-config.service';
import {equals, isNumber} from '../../common/service/data-util';
import {first, Observable, of, tap, throwError} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {NgEventBus} from 'ng-event-bus';
import {EsriModuleService} from '../esri-nlr/services/esri-module.service';
import {ArcGisAuthService} from './arc-gis-auth.service';
import {DEFAULT_MAP_MODE} from './map-mode.service';
import {forkJoinDefaultIfEmpty} from '../../common/observable.utils';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class SpatialUtilService {
    private geometryService: any;
    private featuresUpdatedTimeout: any;

    constructor(
        private esriModuleService: EsriModuleService,
        private featuresRepositoryService: FeaturesRepositoryService,
        private planModelService: PlanModelService,
        private spatialQueryService: SpatialQueryService,
        private lookupService: LookupService,
        private layerDefinitionService: LayerDefinitionService,
        private traitsService: TraitsService,
        private appConfigService: AppConfigService,
        private eventBus: NgEventBus,
        private arcGisAuthService: ArcGisAuthService,
        private ngZone: NgZone
    ) {}

    title = 'Spatial Util Service';
    map = undefined;
    mapMode = DEFAULT_MAP_MODE;

    mapBackground = 'black';

    mapFeatureLayerMap = {};

    featureHighlightsMap = {};

    createMapFeatureLayer(featureClass, options) {
        this.ngZone.runOutsideAngular(() => {
            console.log(`${this.title}: createMapFeatureLayer : ${featureClass}`);
            const layerDefinition = window.structuredClone(this.layerDefinitionService.getLayerDefinition(featureClass));

            if (layerDefinition.extent) {
                layerDefinition.extent.spatialReference = new (window as any).esri.SpatialReference(this.planModelService.getSpatialReference());
            }

            this.mapFeatureLayerMap[featureClass] = (window as any).esri.layers.FeatureLayer(
                {
                    layerDefinition: layerDefinition,
                    featureSet: {
                        features: [],
                        geometryType: layerDefinition.geometryType
                    }
                },
                options
            );
        });
    }

    initMapFeatureLayers() {
        console.log(`${this.title}: initMapFeatureLayers`);
        this.createMapFeatureLayer('line', {id: 'lineFeatureLayer', styling: false});
        this.createMapFeatureLayer('polyline', {id: 'polylineFeatureLayer', styling: false});
        this.createMapFeatureLayer('point', {id: 'pointFeatureLayer'});
        this.createMapFeatureLayer('controlpoint', {id: 'controlPointFeatureLayer'});
        this.createMapFeatureLayer('constructpoint', {id: 'constructPointFeatureLayer'});
        this.createMapFeatureLayer('constructline', {id: 'constructLineFeatureLayer', styling: false});
        this.createMapFeatureLayer('angle', {id: 'angleFeatureLayer'});
        this.createMapFeatureLayer('annotation', {id: 'annotationFeatureLayer'});
        this.createMapFeatureLayer('lot', {id: 'lotFeatureLayer', styling: false});
        this.createMapFeatureLayer('affectedlot', {id: 'affectedLotFeatureLayer'});
        this.createMapFeatureLayer('road', {id: 'roadFeatureLayer', styling: false});
        this.createMapFeatureLayer('interest', {id: 'interestFeatureLayer', styling: false});
        this.createMapFeatureLayer('enlargement', {id: 'enlargementFeatureLayer', styling: false});
        this.createMapFeatureLayer('clipping', {id: 'clippingFeatureLayer', styling: false});
        this.createMapFeatureLayer('printarea', {id: 'printAreaFeatureLayer'});
        this.createMapFeatureLayer('polygon', {id: 'polygonFeatureLayer', styling: false});
        this.initFeatureHighlights();
    }

    getMapBackground() {
        return this.mapBackground;
    }

    setMapBackground(background) {
        this.mapBackground = background;
        this.refreshMapFeatureLayers();
    }

    getBlackModeDefaultCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_SELECT_BLACK + "') 13 13, auto";
    }

    getWhiteModeDefaultCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_SELECT_WHITE + "') 13 13, auto";
    }

    getDefaultCursorUrl() {
        return this.mapBackground === 'black' ? this.getBlackModeDefaultCursorUrl() : this.getWhiteModeDefaultCursorUrl();
    }

    getBlackModePencilCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_PENCIL_BLACK + "') 5 22, auto";
    }

    getWhiteModePencilCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_PENCIL_WHITE + "') 5 22, auto";
    }

    getPencilCursorUrl() {
        return this.mapBackground === 'black' ? this.getBlackModePencilCursorUrl() : this.getWhiteModePencilCursorUrl();
    }

    getBlackModeCrossCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_CROSS_BLACK + "') 13 13, auto";
    }

    getWhiteModeCrossCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_CROSS_WHITE + "') 13 13, auto";
    }

    getCrossCursorUrl() {
        return this.mapBackground === 'black' ? this.getBlackModeCrossCursorUrl() : this.getWhiteModeCrossCursorUrl();
    }

    getBlackModeMoveCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_MOVE_BLACK + "') 13 13, auto";
    }

    getWhiteModeMoveCursorUrl() {
        return "url('" + this.appConfigService.getDesignProperties().DESIGN_CURSORS_CONFIGURATION.CURSOR_MOVE_WHITE + "') 13 13, auto";
    }

    getMoveCursorUrl() {
        return this.mapBackground === 'black' ? this.getBlackModeMoveCursorUrl() : this.getWhiteModeMoveCursorUrl();
    }

    setMapCursor(cursor?: string) {
        cursor = cursor || this.map.cursor;
        if (this.map && cursor) {
            if (this.getMapBackground() === 'black') {
                cursor = cursor.replace(/black/g, 'white');
            } else {
                cursor = cursor.replace(/white/g, 'black');
            }
            this.map.setMapCursor(cursor);
        }
    }

    redrawFeatureLayers() {
        Object.keys(this.mapFeatureLayerMap).forEach(featureClass => this.redrawFeatureLayer(featureClass));
    }

    redrawFeatureLayer(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const mapFeatureLayer = this.getMapFeatureLayer(featureClass);
        mapFeatureLayer.redraw();
    }

    refreshFeatureLayers() {
        Object.keys(this.mapFeatureLayerMap).forEach(featureClass => this.refreshFeatureLayer(featureClass));
    }

    refreshFeatureLayer(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const mapFeatureLayer = this.getMapFeatureLayer(featureClass);
        mapFeatureLayer.refresh();
    }

    clearFeatureLayer(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const mapFeatureLayer = this.getMapFeatureLayer(featureClass);
        mapFeatureLayer.clear();
    }

    clearFeatureLayers(featureClasses) {
        featureClasses = featureClasses || Object.keys(this.mapFeatureLayerMap);
        featureClasses.forEach(featureClass => this.clearFeatureLayer(featureClass));
    }

    getMapMode() {
        return this.mapMode;
    }

    setMapMode(mapMode) {
        this.mapMode = mapMode;
    }

    getMap() {
        return this.map;
    }

    getMapFeatureLayer(featureClassOrLayer) {
        return this.ngZone.runOutsideAngular(() => {
            console.log(`${this.title}: getMapFeatureLayer :${featureClassOrLayer}`);
            const featureClass = this.getFeatureClass(featureClassOrLayer);
            if (this.mapFeatureLayerMap[featureClass]) {
                return this.mapFeatureLayerMap[featureClass];
            }
            const mapLayerId = this.map.graphicsLayerIds.find(v => v.toLowerCase() === `${featureClass}featurelayer`);
            return this.map.getLayer(mapLayerId);
        });
    }

    getAffectedLotMapLayer() {
        return this.getMapFeatureLayer('affectedlot');
    }

    getAngleMapLayer() {
        return this.getMapFeatureLayer('angle');
    }

    getAnnotationMapLayer() {
        return this.getMapFeatureLayer('annotation');
    }

    getClippingMapLayer() {
        return this.getMapFeatureLayer('clipping');
    }

    getConstructLineMapLayer() {
        return this.getMapFeatureLayer('constructline');
    }

    getConstructPointMapLayer() {
        return this.getMapFeatureLayer('constructpoint');
    }

    getControlPointMapLayer() {
        return this.getMapFeatureLayer('controlpoint');
    }

    getEnlargementMapLayer() {
        return this.getMapFeatureLayer('enlargement');
    }

    getInterestMapLayer() {
        return this.getMapFeatureLayer('interest');
    }

    getLineMapLayer() {
        return this.getMapFeatureLayer('line');
    }

    getLotMapLayer() {
        return this.getMapFeatureLayer('lot');
    }

    getPointMapLayer() {
        return this.getMapFeatureLayer('point');
    }

    getPolygonMapLayer() {
        return this.getMapFeatureLayer('polygon');
    }

    getPolylineMapLayer() {
        return this.getMapFeatureLayer('polyline');
    }

    getPrintAreaMapLayer() {
        return this.getMapFeatureLayer('printarea');
    }

    getRoadMapLayer() {
        return this.getMapFeatureLayer('road');
    }

    getMapFeatureLayers() {
        return this.getFeatureClasses().map(featureClass => this.getMapFeatureLayer(featureClass));
    }

    queryFeatures(featureClass) {
        console.debug(`${this.title} - query - ${featureClass}`);
        featureClass = featureClass.toLowerCase();

        const featureLayer = this.getFeatureLayer(featureClass);
        if (!featureLayer) {
            const err = `Feature layer not found for feature type: ${featureClass}`;
            console.error(`${this.title} - query - ${err}`);
            return throwError(err);
        }

        return this.spatialQueryService
            .queryFeatures(featureLayer)
            .pipe(switchMap(features => this.featuresRepositoryService.addFeatures(featureClass, features).pipe(map(() => features))));
    }
    getFeatureClass(featureClassOrLayer) {
        return this.featuresRepositoryService.getFeatureClass(featureClassOrLayer);
    }

    getFeatureClasses() {
        return this.featuresRepositoryService.getFeatureClasses();
    }

    clearFeatureClass(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);

        const featuresToDelete = this.toApplyEditsDeletes(featureClass, this.getFeatures(featureClass));

        if (featuresToDelete.length === 0) {
            return of([]);
        }

        return this.applyEdits(featureClass, null, null, featuresToDelete).pipe(
            tap(res => console.debug(`${this.title} - clearLayer() response:`, res)),
            catchError(err => {
                console.error(`${this.title} - clearLayer() error:`, err);
                return of({error: true, details: err});
            })
        );
    }

    clearFeatureClasses(featureClassesOrLayers) {
        const clearObservables = featureClassesOrLayers.map(v => this.clearFeatureClass(v));
        return forkJoinDefaultIfEmpty(clearObservables);
    }

    getFeatures(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        return this.featuresRepositoryService.getFeatures(featureClass).slice();
    }

    getFeatureIds(featureClassOrLayer) {
        return this.getFeatures(featureClassOrLayer).map(f => f.attributes.id);
    }

    getAllFeatures() {
        return this.featuresRepositoryService.getAllFeatures();
    }

    getAffectedLots() {
        return this.getFeatures('affectedlot');
    }

    getAngles() {
        return this.getFeatures('angle');
    }

    getAnnotations() {
        return this.getFeatures('annotation');
    }

    getClippings() {
        return this.getFeatures('clipping');
    }

    getConstructLines() {
        return this.getFeatures('constructline');
    }

    getConstructPoints() {
        return this.getFeatures('constructpoint');
    }

    getControlPoints() {
        return this.getFeatures('controlpoint');
    }

    getEnlargements() {
        return this.getFeatures('enlargement');
    }

    getInterests() {
        return this.getFeatures('interest');
    }

    getLines() {
        return this.getFeatures('line');
    }

    getLots() {
        return this.getFeatures('lot');
    }

    getChargeableLots() {
        const parcelTypesList = this.lookupService.getLookups('parcelType');
        return this.getLots().filter(lot => {
            const parcelTypeLookup = parcelTypesList.find(parcelType => parcelType.id === lot.attributes.type);
            return parcelTypeLookup.active && parcelTypeLookup.lotFeeChargeable;
        });
    }

    getPoints() {
        return this.getFeatures('point');
    }

    getPolygons() {
        return this.getFeatures('polygon');
    }

    getPolylines() {
        const polylines = this.getFeatures('polyline');
        const polylineIds = polylines.map(f => f.attributes.id);
        return [
            ...polylines,
            ...this.getFeatures('line').filter(f => (f.attributes.lineids || f.attributes.pointids) && !polylineIds.includes(f.attributes.id))
        ];
    }

    getPrintAreas() {
        return this.getFeatures('printarea');
    }

    getRoads() {
        return this.getFeatures('road');
    }

    findByCoords(featuresWithPointGeometry, x, y) {
        return featuresWithPointGeometry.filter(f => f.geometry).find(f => this.coordsEqual(f.geometry.x, x) && this.coordsEqual(f.geometry.y, y));
    }

    findConstructPointByCoords(x, y) {
        return this.findByCoords(this.getConstructPoints(), x, y);
    }

    findControlPointByCoords(x, y) {
        return this.findByCoords(this.getControlPoints(), x, y);
    }

    findPointByCoords(x, y) {
        return this.findByCoords(this.getPoints(), x, y);
    }

    findById(featureClassOrLayer, id) {
        return this.getFeatures(this.getFeatureClass(featureClassOrLayer)).find(f => f.attributes.id === id);
    }

    copyGraphic(graphic) {
        return graphic && graphic.toJson ? new (window as any).esri.Graphic(graphic.toJson()) : graphic;
    }

    copyGeometry(geometry?: any) {
        if (!geometry) {
            return null;
        }
        const geometryJson = geometry.toJson ? geometry.toJson() : structuredClone(geometry);
        const geometryCopy = (window as any).esri.geometry.fromJson(geometryJson);
        return geometryCopy;
    }

    parseFloat(s) {
        // Handle number locale strings containing commas e.g. 1,234,567
        return parseFloat(`${s}`.replace(/,/g, ''));
    }

    toIntLocaleString(s) {
        return Math.floor(this.parseFloat(s)).toLocaleString();
    }

    toFloatLocaleString(s) {
        return this.parseFloat(s).toLocaleString();
    }

    sumPolygonStatedAreas(polygons) {
        const sum = polygons
            .map(p => p.attributes.statedarea)
            .filter(v => v)
            .reduce((acc, v) => acc + this.parseFloat(v), 0);
        return sum > 0 ? sum : 0;
    }

    polygonGeometryUnion(polygonGeometries) {
        return polygonGeometries.length
            ? this.esriModuleService.geometryEngine.union(polygonGeometries)
            : new (window as any).esri.geometry.Polygon();
    }

    clearFeatures(featureClassOrLayer) {
        return this.featuresRepositoryService.clearFeatures(this.getFeatureClass(featureClassOrLayer));
    }

    clearAllFeatures() {
        this.featuresRepositoryService.clearAllFeatures();
    }

    clearAffectedLots() {
        return this.clearFeatures('affectedlot');
    }

    clearAngles() {
        return this.clearFeatures('angle');
    }

    clearAnnotations() {
        return this.clearFeatures('annotation');
    }

    clearClippings() {
        return this.clearFeatures('clipping');
    }

    clearConstructLines() {
        return this.clearFeatures('constructline');
    }

    clearConstructPoints() {
        return this.clearFeatures('constructpoint');
    }

    clearControlPoints() {
        return this.clearFeatures('controlpoint');
    }

    clearEnlargements() {
        return this.clearFeatures('enlargement');
    }

    clearInterests() {
        return this.clearFeatures('interest');
    }

    clearLines() {
        return this.clearFeatures('line');
    }

    clearLots() {
        return this.clearFeatures('lot');
    }

    clearPoints() {
        return this.clearFeatures('point');
    }

    clearPolygons() {
        return this.clearFeatures('polygon');
    }

    clearPolylines() {
        return this.clearFeatures('polyline');
    }

    clearPrintAreas() {
        return this.clearFeatures('printarea');
    }

    clearRoads() {
        return this.clearFeatures('road');
    }

    refreshFeatures() {
        return this.featuresRepositoryService.refresh();
    }

    getGeometryFeatureCount() {
        return this.featuresRepositoryService.getGeometryFeatureCount();
    }

    getFeatureCount() {
        return this.featuresRepositoryService.getFeatureCount(this.getFeatureClasses());
    }

    graphicsFilters = {};

    setGraphicsFilter(featureClassOrLayer, filter) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        this.graphicsFilters[featureClass] = filter;
    }

    getGraphics(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);

        const features = (featureClass === 'polyline' && this.getPolylines()) || this.getFeatures(featureClass);

        const graphicsFilter = this.graphicsFilters[featureClass];
        if (graphicsFilter) {
            return features.filter(graphicsFilter).sort((f0, f1) => f0.attributes.id - f1.attributes.id);
        }
        return features;
    }

    getGraphicIds(featureClassOrLayer) {
        return this.getGraphics(featureClassOrLayer).map(g => g.attributes.id);
    }

    getAffectedLotGraphics() {
        return this.getGraphics('affectedlot');
    }

    getAngleGraphics() {
        return this.getGraphics('angle');
    }

    getAnnotationGraphics() {
        return this.getGraphics('annotation');
    }

    getClippingGraphics() {
        return this.getGraphics('clipping');
    }

    getConstructLineGraphics() {
        return this.getGraphics('constructline');
    }

    getConstructPointGraphics() {
        return this.getGraphics('constructpoint');
    }

    getControlPointGraphics() {
        return this.getGraphics('controlpoint');
    }

    getEnlargementGraphics() {
        return this.getGraphics('enlargement');
    }

    getInterestGraphics() {
        return this.getGraphics('interest');
    }

    getLineGraphics() {
        return this.getGraphics('line');
    }

    getLotGraphics() {
        return this.getGraphics('lot');
    }

    getPointGraphics() {
        return this.getGraphics('point');
    }

    getPolygonGraphics() {
        return this.getGraphics('polygon');
    }

    getPolygonGraphicsByParentFeature(parentFeature: any) {
        if (parentFeature.attributes.polygonids) {
            return this.getPolygonGraphics().filter((p: any) => parentFeature.attributes.polygonids.includes(`[${p.attributes.id}]`));
        }
        return [];
    }

    getPolylineGraphics() {
        return this.getGraphics('polyline');
    }

    getPrintAreaGraphics() {
        return this.getGraphics('printarea');
    }

    getRoadGraphics() {
        return this.getGraphics('road');
    }

    getHighlightedFeatures(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const highlightedFeatureIds = this.featureHighlightsMap[featureClass];

        const featureClasses = featureClass === 'controlpoint' ? ['controlpoint', 'point'] : [featureClass];
        const featureGraphics = featureClasses.reduce((acc, fc) => {
            acc.push(...this.getGraphics(fc));
            return acc;
        }, []);

        return highlightedFeatureIds.map(id => featureGraphics.find(f => f.attributes.id === id)).filter(v => v);
    }

    getHighlightedFeatureSets(featureClassesOrLayers?) {
        const mapFeatureLayers = (featureClassesOrLayers || this.map.graphicsLayerIds).map(v => this.getMapFeatureLayer(v));
        return mapFeatureLayers.map(v => ({featureLayer: v, graphics: this.getHighlightedFeatures(v)}));
    }

    getMapGraphics(featureClassOrLayer, featuresOrFeatureIds) {
        const mapGraphics = this.getMapGraphicsArray();
        if (mapGraphics.length === 0) {
            return [];
        }

        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const featureIds = (featuresOrFeatureIds || this.getHighlightedFeatures(featureClass)).map(v => (isNumber(v) ? v : v.attributes.id));

        const featureClasses = ['controlpoint', 'point'].includes(featureClass) ? ['controlpoint', 'point'] : [featureClass];
        return mapGraphics.filter(g => g.attributes && featureClasses.includes(g.attributes.featureClass) && featureIds.includes(g.attributes.id));
    }

    toFeatureIds(featuresOrFeatureIds) {
        return (featuresOrFeatureIds || [])
            .filter(v => v)
            .map(v => (isNumber(v) ? v : v.attributes.id))
            .filter(v => isNumber(v));
    }

    getFeatureMap(features) {
        return features.reduce((acc, f) => {
            acc[f.attributes.id] = f;
            return acc;
        }, {});
    }

    updateMapGraphicGeometries(featureClassOrLayer, featuresOrFeatureIds) {
        const mapGraphics = this.getMapGraphics(featureClassOrLayer, featuresOrFeatureIds);
        if (mapGraphics.length === 0) {
            return;
        }
        const mapGraphicsMap = this.getFeatureMap(mapGraphics);
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const featureIds = this.toFeatureIds(featuresOrFeatureIds);
        const features = this.getGraphics(featureClass).filter(
            f => featureIds.includes(f.attributes.id) && f.geometry && mapGraphicsMap[f.attributes.id]
        );
        if (features.length) {
            features.forEach(f => (mapGraphicsMap[f.attributes.id].geometry = (window as any).esri.geometry.fromJson(f.geometry.toJson())));
            this.redrawMapGraphics();
        }
    }

    getHighlightedAffectedLots() {
        return this.getHighlightedFeatures('affectedlot');
    }

    getHighlightedAngles() {
        return this.getHighlightedFeatures('angle');
    }

    getHighlightedAnnotations() {
        return this.getHighlightedFeatures('annotation');
    }

    getHighlightedClippings() {
        return this.getHighlightedFeatures('clipping');
    }

    getHighlightedConstructLines() {
        return this.getHighlightedFeatures('constructline');
    }

    getHighlightedConstructPoints() {
        return this.getHighlightedFeatures('constructpoint');
    }

    getHighlightedControlPoints() {
        const controlPoints = this.getHighlightedFeatures('controlpoint');
        if (controlPoints.length) {
            return controlPoints;
        }
        const controlPointIds = this.getGraphicIds('controlpoint');
        return this.getHighlightedFeatures('point').filter(f => controlPointIds.includes(f.attributes.id));
    }

    getHighlightedEnlargements() {
        return this.getHighlightedFeatures('enlargement');
    }

    getHighlightedInterests() {
        return this.getHighlightedFeatures('interest');
    }

    getHighlightedLines() {
        return this.getHighlightedFeatures('line');
    }

    getHighlightedLots() {
        return this.getHighlightedFeatures('lot');
    }

    getHighlightedPoints() {
        return this.getHighlightedFeatures('point');
    }

    getHighlightedPolygons() {
        return this.getHighlightedFeatures('polygon');
    }

    getHighlightedPolylines() {
        const polylines = this.getHighlightedFeatures('polyline');
        const polylineIds = polylines.map(f => f.attributes.id);
        return [
            ...polylines,
            ...this.getHighlightedFeatures('line').filter(
                f => (f.attributes.lineids || f.attributes.pointids) && !polylineIds.includes(f.attributes.id)
            )
        ];
    }

    getHighlightedPrintAreas() {
        return this.getHighlightedFeatures('printarea');
    }

    getHighlightedRoads() {
        return this.getHighlightedFeatures('road');
    }

    isHighlightedFeature(featureClassOrLayer, featureOrFeatureId) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const featureId = isNumber(featureOrFeatureId) ? featureOrFeatureId : featureOrFeatureId.attributes.id;
        const lineClasses = ['line', 'polyline'];
        return (lineClasses.includes(featureClass) ? lineClasses : [featureClass]).some(
            fc => this.featureHighlightsMap[fc] && this.featureHighlightsMap[fc].includes(featureId)
        );
    }

    getFeatureHighlightsMap() {
        return this.featureHighlightsMap;
    }

    getHighlightedFeatureIds(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        return this.featureHighlightsMap[featureClass].slice();
    }

    someFeaturesHighlighted(featureClassOrLayer, featuresOrFeatureIds) {
        const featureIds = this.toFeatureIds(featuresOrFeatureIds);
        const highlightedFeatureIds = this.getHighlightedFeatureIds(featureClassOrLayer);
        return featureIds.some(featureId => highlightedFeatureIds.includes(featureId));
    }

    highlightFeatures(featureClassOrLayer, featuresOrFeatureIds?) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const featureHighlights = this.featureHighlightsMap[featureClass];
        const oldFeatureHighlightsMap = window.structuredClone(this.featureHighlightsMap);

        const featureIds = (featuresOrFeatureIds ? this.toFeatureIds(featuresOrFeatureIds) : this.getFeatureIds(featureClass)).filter(
            v => !featureHighlights.includes(v)
        );

        if (featureIds.length) {
            featureHighlights.push(...featureIds);
        }

        if (!equals(this.featureHighlightsMap, oldFeatureHighlightsMap)) {
            console.debug('featureHighlightsChanged');
            this.eventBus.cast('featureHighlightsChanged', oldFeatureHighlightsMap);
        }
    }

    highlightFeature(featureClassOrLayer, featureOrFeatureId) {
        this.highlightFeatures(featureClassOrLayer, [featureOrFeatureId]);
    }

    highlightAllFeatures() {
        this.getFeatureClasses().forEach(k => this.highlightFeatures(k));
    }

    highlightAllGeometryFeatures() {
        this.getGeometryFeatureClasses().forEach(featureClass => this.highlightFeatures(featureClass));
    }

    unhighlightFeatures(featureClassOrLayer: any, featuresOrFeatureIds?: any[]) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const featureHighlights = this.featureHighlightsMap[featureClass];
        const oldFeatureHighlightsMap = window.structuredClone(this.featureHighlightsMap);

        if (featuresOrFeatureIds) {
            const featureIds = this.toFeatureIds(featuresOrFeatureIds);
            const featureIdsToKeep = featureHighlights.filter(v => !featureIds.includes(v));

            if (featureIdsToKeep.length < featureHighlights.length) {
                featureHighlights.splice(0, featureHighlights.length, ...featureIdsToKeep);
            }
        } else {
            featureHighlights.length = 0;
        }

        if (!equals(this.featureHighlightsMap, oldFeatureHighlightsMap)) {
            console.log('featureHighlightsChanged');
            this.eventBus.cast('featureHighlightsChanged', oldFeatureHighlightsMap);
        }
    }

    unhighlightFeature(featureClassOrLayer, featureOrFeatureId) {
        this.unhighlightFeatures(featureClassOrLayer, [featureOrFeatureId]);
    }

    unhighlightAllFeatures(featureClasses?) {
        featureClasses = featureClasses || this.getFeatureClasses();
        const oldFeatureHighlightsMap = window.structuredClone(this.featureHighlightsMap);
        featureClasses.forEach(k => {
            if (this.featureHighlightsMap[k]) {
                this.featureHighlightsMap[k].length = 0;
            }
        });
        if (!equals(this.featureHighlightsMap, oldFeatureHighlightsMap)) {
            console.log('featureHighlightsChanged');
            this.eventBus.cast('featureHighlightsChanged', oldFeatureHighlightsMap);
        }
    }

    unhighlightAllGeometryFeatures() {
        this.unhighlightAllFeatures(this.getGeometryFeatureClasses());
    }

    unhighlightAllNonBusinessFeatures() {
        this.unhighlightAllFeatures(this.getNonBusinessFeatureClasses());
    }

    initFeatureHighlights() {
        this.getFeatureClasses().forEach(k => (this.featureHighlightsMap[k] = []));
    }

    getFeatureHighlightsCount(featureClasses?) {
        return (featureClasses || this.getFeatureClasses()).reduce((acc, k) => acc + this.featureHighlightsMap[k].length, 0);
    }

    getBusinessFeatureClasses() {
        return ['affectedlot', 'interest', 'lot', 'road'];
    }

    getNonBusinessFeatureClasses() {
        const businessFeatureClasses = this.getBusinessFeatureClasses();
        return this.getFeatureClasses().filter(fc => !businessFeatureClasses.includes(fc));
    }

    getGeometryFeatureClasses() {
        return this.featuresRepositoryService.getGeometryFeatureClasses();
    }

    getGeometryFeatureHighlightsCount() {
        return this.getFeatureHighlightsCount(this.featuresRepositoryService.getGeometryFeatureClasses());
    }

    isFeatureHidden(featureClassOrLayer, featureOrFeatureId) {
        const mapMode = this.getMapMode();
        if (!mapMode) {
            return false;
        }

        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const featureId = isNumber(featureOrFeatureId) ? featureOrFeatureId : featureOrFeatureId.attributes.id;

        const feature = this.getFeatures(featureClass).find(f => f.attributes.id === featureId);
        if (!feature) {
            return false;
        }

        if (mapMode.type === 'mainPlan') {
            return feature.attributes.visible === 'NO';
        }

        return feature.attributes.hiddenonenlargements && feature.attributes.hiddenonenlargements.includes(`[${mapMode.id}]`);
    }

    isFeatureVisible(featureClassOrLayer, featureOrFeatureId) {
        return !this.isFeatureHidden(featureClassOrLayer, featureOrFeatureId);
    }

    getFeatureLayerName(featureLayer) {
        //console.debug(this.title + ' : getFeatureLayerName  : ' + featureLayer.id);

        // treat all<something>FeatureLayers and <something>FeatureLayers identically so that
        // this method doesn't break as more all<something>FeatureLayers are added
        let sanitisedFeatureLayerId = featureLayer.id;
        const matchResults = featureLayer.id.match(/^all([A-Z].+FeatureLayer)$/);
        if (matchResults && matchResults.length === 2) {
            const capturedGroup = matchResults[1];
            sanitisedFeatureLayerId = capturedGroup[0].toLowerCase() + capturedGroup.slice(1);
        }

        let output = '';

        switch (sanitisedFeatureLayerId) {
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.ANGLE.OPTIONS.id:
                output = 'angle';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.ANNOTATION.OPTIONS.id:
                output = 'annotation';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.POINT.OPTIONS.id:
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.CONTROL_POINT.OPTIONS.id:
                output = 'point';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.CONSTRUCT_POINT.OPTIONS.id:
                output = 'constructPoint';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.CONSTRUCT_LINE.OPTIONS.id:
                output = 'constructLine';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.LINE.OPTIONS.id:
                output = 'line';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.POLYGON.OPTIONS.id:
                output = 'polygon';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.LOT.OPTIONS.id:
                output = 'lot';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.AFFECTED_LOT.OPTIONS.id:
                output = 'affectedLot';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.ROAD.OPTIONS.id:
                output = 'road';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.INTEREST.OPTIONS.id:
                output = 'interest';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.INTEREST_LINE.OPTIONS.id:
                output = 'polyline';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.ENLARGEMENT.OPTIONS.id:
                output = 'enlargement';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.CLIPPING.OPTIONS.id:
                output = 'clipping';
                break;
            case this.appConfigService.getDesignProperties().DESIGN_SETTINGS.DEFAULT_MAP.LAYERS.PRINT_AREA.OPTIONS.id:
                output = 'printArea';
                break;
        }

        return output;
    }

    getMapGraphicsArray() {
        return (this.map && this.map.graphics && this.map.graphics.graphics) || [];
    }

    addMapGraphic(featureClassOrLayer, graphic) {
        if (featureClassOrLayer) {
            const featureClass = this.getFeatureClass(featureClassOrLayer);
            const featureLayer = this.getFeatureLayer(featureClass);
            if (featureLayer && graphic.attributes) {
                const featureId = graphic.attributes.id;
                if (featureId) {
                    const fc = featureClass === 'point' && this.getFeatureIds('controlpoint').includes(featureId) ? 'controlpoint' : featureClass;
                    this.highlightFeature(fc, graphic.attributes.id);
                    if (['point', 'controlpoint', 'constructpoint', 'constructline', 'line', 'polyline', 'polygon'].includes(fc)) {
                        return; // Don't add map graphic
                    }
                }

                graphic.attributes.featureClass = featureClass;
                // Add nullable fields if not present (exclude field names starting with "attribute")
                if (featureLayer.fields) {
                    featureLayer.fields
                        .filter(
                            f => f.nullable && !f.name.startsWith('attribute') && !Object.prototype.hasOwnProperty.call(graphic.attributes, f.name)
                        )
                        .forEach(f => (graphic.attributes[f.name] = null));
                }
            }
        }

        if (this.map && this.map.graphics) {
            this.map.graphics.add(graphic);
        }
    }

    clearMapGraphics() {
        if (this.map && this.map.graphics) {
            this.map.graphics.clear();
            this.unhighlightAllGeometryFeatures();
        }
    }

    redrawMapGraphics() {
        if (this.map && this.map.graphics) {
            this.map.graphics.redraw();
        }
    }

    removeMapGraphic(graphic) {
        if (this.map && this.map.graphics) {
            this.map.graphics.remove(graphic);
            if (graphic.attributes && graphic.attributes.featureClass) {
                this.unhighlightFeature(graphic.attributes.featureClass, graphic.attributes.id);
            }
        }
    }

    featureClassesUpdated = [];

    refreshMapLayer(mapLayer) {
        if (!mapLayer) {
            return;
        }

        const featureClass = this.getFeatureClass(mapLayer);
        if (featureClass) {
            const mapLayerGraphics = mapLayer.graphics;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const mapLayerGraphicIds = mapLayerGraphics.map(g => g.attributes.id).filter((v, i, a) => a.indexOf(v) === i);
            const mapLayerGraphicMap = mapLayerGraphics.reduce((acc, g) => {
                acc[g.attributes.id] = g;
                return acc;
            }, {});

            const {graphicsToKeep, graphicsToAdd} = this.getGraphics(featureClass).reduce(
                (acc, g) => {
                    const graphicJson = g.toJson();
                    if (isNumber(graphicJson.uniqueId)) {
                        delete graphicJson.uniqueId;
                    }

                    if (graphicJson.attributes && isNumber(graphicJson.attributes.uniqueId)) {
                        delete graphicJson.attributes.uniqueId;
                    }

                    const mapLayerGraphic = mapLayerGraphicMap[g.attributes.id];
                    const mapLayerGraphicJson = mapLayerGraphic && mapLayerGraphic.toJson();
                    if (mapLayerGraphicJson) {
                        if (isNumber(mapLayerGraphicJson.uniqueId)) {
                            delete mapLayerGraphicJson.uniqueId;
                        }
                        if (mapLayerGraphicJson.attributes && isNumber(mapLayerGraphicJson.attributes.uniqueId)) {
                            delete mapLayerGraphicJson.attributes.uniqueId;
                        }
                    }

                    if (mapLayerGraphic && mapLayerGraphic.attributes.id === g.attributes.id && equals(mapLayerGraphicJson, graphicJson)) {
                        acc.graphicsToKeep.push(mapLayerGraphic);
                    } else {
                        // If graphic is in another layer (e.g. polyline in line and polyline layers), add a new graphic to avoid uniqueId conflicts
                        const graphicLayer = g.getLayer();
                        const graphicToAdd = graphicLayer && graphicLayer !== mapLayer ? new (window as any).esri.Graphic(graphicJson) : g;
                        Object.keys(graphicToAdd)
                            .filter(k => k.startsWith('_'))
                            .forEach(k => delete graphicToAdd[k]);
                        acc.graphicsToAdd.push(graphicToAdd);
                    }
                    return acc;
                },
                {graphicsToKeep: [], graphicsToAdd: []}
            );

            const graphics = [...graphicsToKeep, ...graphicsToAdd].sort((g0, g1) => g0.attributes.id - g1.attributes.id);
            const graphicIds = graphics.map(g => g.attributes.id);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const graphicsToDelete = mapLayerGraphics.filter(g => !graphicIds.includes(g.attributes.id));
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const graphicsUpdated = graphicsToAdd.length || graphics.length !== mapLayerGraphics.length;

            mapLayerGraphics.splice(0, mapLayerGraphics.length, ...graphics);

            if (graphicsUpdated) {
                // Sometimes duplicates appear in the map layer graphics so the map layer must be redrawn even though features haven't been updated
                const featuresUpdated = graphicsToAdd.length || graphics.length !== mapLayerGraphicIds.length;

                new Observable(subscriber => {
                    if (Object.keys(this.mapFeatureLayerMap).includes(featureClass)) {
                        mapLayer.applyEdits(graphicsToAdd, graphicsToKeep, graphicsToDelete).then(() => {
                            subscriber.next(null);
                            subscriber.complete();
                        });
                    } else {
                        subscriber.next(null);
                        subscriber.complete();
                    }
                })
                    .pipe(
                        first(),
                        tap(() => {
                            if (mapLayerGraphics.length === 0) {
                                mapLayer.clear();
                            } else {
                                mapLayer.redraw();
                            }
                            if (this.getNonBusinessFeatureClasses().includes(featureClass) || featuresUpdated) {
                                if (this.featuresUpdatedTimeout) {
                                    clearTimeout(this.featuresUpdatedTimeout);
                                }
                                if (!this.featureClassesUpdated.includes(featureClass)) {
                                    this.featureClassesUpdated.push(featureClass);
                                }
                                this.featuresUpdatedTimeout = setTimeout(() => {
                                    this.eventBus.cast('featuresUpdated', this.featureClassesUpdated.slice());
                                    this.featureClassesUpdated.length = 0;
                                }, 100);
                            }
                        })
                    )
                    .subscribe();
            }
        } else {
            mapLayer.refresh();
        }
    }

    refreshMapFeatureLayer(featureClassOrLayer) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const mapFeatureLayer = this.getMapFeatureLayer(featureClass);
        // refresh the map layer within a timeout to ensure display is refreshed
        setTimeout(() => this.refreshMapLayer(mapFeatureLayer), 0);
    }

    refreshMapFeatureLayers() {
        Object.keys(this.mapFeatureLayerMap).forEach(featureClass => this.refreshMapFeatureLayer(featureClass));
    }

    applyEdits(featureClassOrLayer, adds, updates, deletes) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const options = Object.keys(this.mapFeatureLayerMap).includes(featureClass) ? {client: true} : {};

        return this.featuresRepositoryService.applyEdits(featureClass, adds, updates, deletes, options).pipe(
            tap(() => {
                // Find associated feature classes and refresh the map for each
                (
                    [
                        ['point', 'controlpoint'],
                        ['line', 'polyline']
                    ].find(f => f.includes(featureClass)) || [featureClass]
                ).forEach(fc => this.refreshMapFeatureLayer(fc));
            })
        );
    }

    toApplyEditsDelete(featureClassOrLayer, featureOrAttrs) {
        const featureClass = this.getFeatureClass(featureClassOrLayer);
        const featureIds = this.getFeatureIds(featureClass);
        const attrs = featureOrAttrs.attributes || featureOrAttrs;
        if (featureIds.includes(attrs.id)) {
            return {attributes: {objectid: attrs.objectid, id: attrs.id}};
        }
        return null;
    }

    toApplyEditsDeletes(featureClassOrLayer, featureOrAttrsList) {
        return featureOrAttrsList.map(featureOrAttrs => this.toApplyEditsDelete(featureClassOrLayer, featureOrAttrs)).filter(v => v);
    }

    hasEdits() {
        return this.featuresRepositoryService.hasEdits();
    }

    interestHasUnsavedAssociations(interestId) {
        return this.featuresRepositoryService.interestHasUnsavedAssociations(interestId);
    }

    save() {
        return this.featuresRepositoryService.save();
    }

    clearAddedDeletedIds(featureClass) {
        this.featuresRepositoryService.clearAddedDeletedIds(featureClass);
    }

    getAddedDeletedIds(featureClass) {
        return this.featuresRepositoryService.getAddedDeletedIds(featureClass);
    }

    getFeatureLayer(featureClass) {
        return this.featuresRepositoryService.getFeatureLayer(featureClass);
    }

    toIdsAttr(ids) {
        return ids.length ? ids.map(v => `[${v}]`).join() : null;
    }

    parseIdsAttr(s?: string): number[] {
        return s
            ? s
                  .replace(/[[\]]/g, '')
                  .split(',')
                  .map(v => parseInt(v))
                  .filter(v => !isNaN(v))
            : [];
    }

    addToIdsAttr(s, id) {
        if (!s) {
            return `[${id}]`;
        }

        id = parseInt(id);
        const ids = this.parseIdsAttr(s);
        const idx = ids.indexOf(id);
        if (idx !== -1) {
            // Already added
            return s;
        }

        ids.push(id);

        return this.toIdsAttr(ids);
    }

    removeFromIdsAttr(s, id) {
        if (!s) {
            return s;
        }

        id = parseInt(id);
        const ids = this.parseIdsAttr(s);
        const idx = ids.indexOf(id);
        if (idx === -1) {
            // Nothing to remove
            return s;
        }

        ids.splice(idx, 1);

        return this.toIdsAttr(ids);
    }

    updateIdsAttr(s, id, remove) {
        return remove ? this.removeFromIdsAttr(s, id) : this.addToIdsAttr(s, id);
    }

    deleteAttrs(attrs, keys) {
        if (keys.includes('id')) {
            // If "id" is present, also delete objectid and uniqueId
            keys = [...keys, ...['objectid', 'uniqueId'].filter(k => !keys.includes(k))];
        }
        keys.filter(k => attrs[k]).forEach(k => delete attrs[k]);
        return attrs;
    }

    getGeometryService() {
        if (!this.geometryService) {
            this.geometryService = new (window as any).esri.tasks.GeometryService(
                this.appConfigService.getDesignProperties().GEOMETRY_SERVER_URL + this.arcGisAuthService.getTokenParam()
            );
        }
        return this.geometryService;
    }

    tolEquals(d0, d1, tolerance) {
        return Math.abs(d0 - d1) < tolerance;
    }

    coordsEqual(c0, c1) {
        return this.tolEquals(c0, c1, c0 <= 180 && c1 <= 180 ? 0.000001 : 0.1);
    }

    project(geometryOrGeometries, spatialReference?) {
        console.debug(`${this.title} : project`);

        return this.esriModuleService.projection.project(
            geometryOrGeometries,
            new (window as any).esri.SpatialReference(spatialReference || this.planModelService.getSpatialReference())
        );
    }

    projectFeatures(features, spatialReference?) {
        console.debug(`${this.title} : projectFeatures`);

        const planGuid = this.planModelService.getGuid();
        spatialReference = spatialReference || this.planModelService.getSpatialReference();

        features.forEach(f => (f.attributes.guid = planGuid));

        const featuresWithGeometry = features.filter(f => f.geometry);

        if (
            featuresWithGeometry.every(
                f =>
                    f.geometry.spatialReference &&
                    ((f.geometry.spatialReference.wkid && f.geometry.spatialReference.wkid === spatialReference.wkid) ||
                        (f.geometry.spatialReference.wkt && f.geometry.spatialReference.wkt === spatialReference.wkt))
            )
        ) {
            return features;
        }

        const geometries = featuresWithGeometry.map(f => f.geometry);

        const tol = 0.001;

        const projectedGeometries = this.project(geometries, spatialReference);

        geometries.forEach((g, i) => {
            const f = featuresWithGeometry[i];
            const pg = projectedGeometries[i] ? projectedGeometries[i] : g;

            if (
                !(
                    (g.type === 'point' && this.tolEquals(g.x, pg.x, tol) && this.tolEquals(g.y, pg.y, tol)) ||
                    (g.type === 'polyline' &&
                        this.tolEquals(g.paths[0][0][0], pg.paths[0][0][0], tol) &&
                        this.tolEquals(g.paths[0][0][1], pg.paths[0][0][1], tol) &&
                        this.tolEquals(g.paths[0][1][0], pg.paths[0][1][0], tol) &&
                        this.tolEquals(g.paths[0][1][1], pg.paths[0][1][1], tol))
                )
            ) {
                f.geometry = pg;
            }
        });

        return features;
    }

    distance(geom1, geom2, units: string = 'meters') {
        const g1 =
            geom1.spatialReference && geom1.spatialReference.wkid === 4283 ? (window as any).esri.geometry.geographicToWebMercator(geom1) : geom1;
        const g2 =
            geom2.spatialReference && geom2.spatialReference.wkid === 4283 ? (window as any).esri.geometry.geographicToWebMercator(geom2) : geom2;
        return this.esriModuleService.geometryEngine.distance(g1, g2, units);
    }

    geodesicDistance(geometry1: any, geometry2: any, units: string = 'meters') {
        const projectedSpatialReference = {wkid: 4326};

        const projectedGeometry1 = this.project(geometry1, projectedSpatialReference);
        const projectedGeometry2 = this.project(geometry2, projectedSpatialReference);

        const polyline = new (window as any).esri.geometry.Polyline({
            paths: [
                [
                    [projectedGeometry1.x, projectedGeometry1.y],
                    [projectedGeometry2.x, projectedGeometry2.y]
                ]
            ],
            spatialReference: projectedSpatialReference
        });

        return this.esriModuleService.geometryEngine.geodesicLength(polyline, units);
    }

    geodesicAreas(polygonGeometries) {
        const geometries = polygonGeometries.map(g => new (window as any).esri.geometry.Polygon(g.toJson()));
        const geographicPolygonGeometries = this.project(geometries, {wkid: 4283});
        const zeroAreaIndexes = geographicPolygonGeometries.map((v, i) => (v == null ? i : -1)).filter(v => v !== -1);
        const filteredGeometries = geographicPolygonGeometries.filter(g => g);
        const geometryAreas = this.esriModuleService.geodesicUtils.geodesicAreas(filteredGeometries, (window as any).esri.Units.SQUARE_METERS);
        zeroAreaIndexes.forEach(idx => geometryAreas.splice(idx, 0, 0));
        return geometryAreas;
    }

    // onEditsComplete(featureLayer, evt) {
    //     this.featuresRepositoryService.onEditsComplete(this.getFeatureClass(featureLayer), evt);
    // }

    getPointTypes(points) {
        const pointTypes = [];
        const pointTypeLookup = this.lookupService.getLookups('pointType');
        points.forEach(p => {
            pointTypeLookup.map(pointType => {
                if (p.attributes.type === pointType.id) {
                    pointTypes.push(p);
                }
            });
        });
        return pointTypes;
    }

    getControlPointTypes(points) {
        const controlPointTypes = [];
        const controlPointTypeLookup = this.lookupService.getLookups('controlPointType');
        points.forEach(p => {
            controlPointTypeLookup.map(pointType => {
                if (p.attributes.type === pointType.id) {
                    controlPointTypes.push(p);
                }
            });
        });
        return controlPointTypes;
    }

    // Delete annotations for roads that are no longer linked to any polygons
    deleteInvalidRoadAnnotations() {
        const roadIds = this.getRoads()
            .filter(r => !r.attributes.polygonids)
            .map(r => r.attributes.id);
        const polygonIds = this.getPolygons().map(p => p.attributes.id);

        const roadAnnotationsToDelete = this.getAnnotations().filter(
            a =>
                a.attributes.featuretype === 'road' &&
                ((!a.attributes.childtype && roadIds.includes(a.attributes.featureid)) ||
                    (a.attributes.childtype === 'polygon' && !polygonIds.includes(a.attributes.childid)))
        );

        return roadAnnotationsToDelete.length ? this.applyEdits('annotation', null, null, roadAnnotationsToDelete) : of(null);
    }

    // Delete annotations for interests that are no longer linked to any polygons or polylines
    deleteInvalidInterestAnnotations() {
        const unlinkedInterestIds = this.getInterests()
            .filter(i => !i.attributes.polygonids && !i.attributes.polylineids)
            .map(id => id.attributes.id);

        const interestAnnotationsToDelete = this.getAnnotations().filter(
            a => a.attributes.featuretype === 'interest' && unlinkedInterestIds.includes(a.attributes.featureid)
        );

        return interestAnnotationsToDelete.length ? this.applyEdits('annotation', null, null, interestAnnotationsToDelete) : of(null);
    }

    initialiseQueryFeatures(featureClass: string): Observable<void> {
        return this.spatialQueryService
            .queryFeatures(this.getFeatureLayer(featureClass))
            .pipe(switchMap(features => this.featuresRepositoryService.addFeatures(featureClass, features)));
    }
    groupContinuousNumbers(numArray) {
        if (!numArray || numArray.length === 0) {
            return [];
        }

        return numArray
            .sort((l1, l2) => l1 - l2)
            .reduce((acc, v, i, arr) => {
                if (!i || arr[i - 1] + 1 !== v) {
                    acc.push([v]);
                } else {
                    acc[acc.length - 1][1] = v;
                }
                return acc;
            }, [])
            .map(arr => arr.join('-'));
    }

    getDefaultLotAcquisitionTransferAction(lot, acquisitionTransferActionLookups) {
        const lotAcquisitionTransferActionLookups = acquisitionTransferActionLookups.filter(l => l.validParcelTypeId === lot.attributes.type);

        if (
            lot.attributes.acquisitiontransferaction &&
            lotAcquisitionTransferActionLookups.map(l => l.id).includes(parseInt(lot.attributes.acquisitiontransferaction))
        ) {
            return `${lot.attributes.acquisitiontransferaction}`;
        }

        let defaultAcquisitionTransferActionLookup;
        if (lotAcquisitionTransferActionLookups.length === 1) {
            defaultAcquisitionTransferActionLookup = lotAcquisitionTransferActionLookups[0];
        } else if (['FH_SUBD', 'CR_SUBD'].includes(this.planModelService.getModel().planPurpose.id)) {
            defaultAcquisitionTransferActionLookup = lotAcquisitionTransferActionLookups.find(l => l.description === 'Not applicable');
        }

        if (defaultAcquisitionTransferActionLookup) {
            return `${defaultAcquisitionTransferActionLookup.id}`;
        }

        return null;
    }

    setLotAcquisitionTransferActionDefaults(lots?: any[]) {
        if (!this.planModelService.getModel().planPurpose) {
            return;
        }

        const acquisitionTransferActionLookups = this.lookupService
            .getLookups('acquisitionTransferAction')
            .filter(l => this.traitsService.criteriaMatches(l.planTraitCriteria, false));

        if (lots && lots.length) {
            lots.forEach(
                lot => (lot.attributes.acquisitiontransferaction = this.getDefaultLotAcquisitionTransferAction(lot, acquisitionTransferActionLookups))
            );
            return;
        }

        const lotUpdates = this.getLots().reduce((acc, lot) => {
            const defaultLotAcquisitionTransferAction = this.getDefaultLotAcquisitionTransferAction(lot, acquisitionTransferActionLookups);
            if ((lot.attributes.acquisitiontransferaction || null) !== defaultLotAcquisitionTransferAction) {
                const lotUpdate = this.copyGraphic(lot);
                lotUpdate.attributes.acquisitiontransferaction = defaultLotAcquisitionTransferAction;
                acc.push(lotUpdate);
            }
            return acc;
        }, []);

        if (lotUpdates.length) {
            this.applyEdits('lot', null, lotUpdates, null)
                .pipe(switchMap(() => this.save()))
                .subscribe();
        }
    }

    initialiseAllFeatureClasses(): Observable<any[]> {
        const featureClassObservables = this.getFeatureClasses().map(featureClass => this.initialiseQueryFeatures(featureClass));
        return forkJoinDefaultIfEmpty(featureClassObservables);
    }
    /**
     * Initialise service
     */
    init() {
        console.log('init spatialUtilService');
        return this.initialiseAllFeatureClasses().pipe(tap(() => this.initMapFeatureLayers()));
    }
    //     console.debug(`${this.title} - init()`);
    //     return from(this.initEsriModules()).pipe(
    //         tap(() => {
    //             this.featuresRepositoryService.init();
    //             this.initialiseAllFeatureClasses();
    //             this.initMapFeatureLayers();
    //         })
    //     );
    // }
    // $rootScope.$on('featuresUpdated', () => {
    //     if (deleteAllSaveTimeout) {
    //         $timeout.cancel(deleteAllSaveTimeout);
    //     }
    //     deleteAllSaveTimeout = $timeout(() => {
    //         if (this.featuresRepositoryService.hasEdits() && this.featuresRepositoryService.getGeometryFeatureCount() === 0) {
    //             // Perform a save when all features have been deleted
    //             return this.save();
    //         }
    //     }, 1000);
    // });
}
